import { Actions as AUTHENTICATION_ACTIONS } from "@/redux/actions/authenticationActions";
import { Actions as COMPANY_ACTIONS } from "@/redux/actions/companyActions";
import { Actions as EMPLOYEEACTIONS } from "@/redux/actions/employeeActions";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import cleanDeep from "clean-deep";
import { cloneDeep, merge, setWith } from "lodash";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import Spinner from "../../common/Spinner";

const mfaOptions = [
	{ label: "Admins & Managers", value: 1 },
	{ label: "All Employees & Admins", value: 2 },
];

const AccessSettings = ({
	updateCompanyDetails,
	currentOrg,
	settings,
	saving,
	setMFAModalStatus,
	hasMFA,
	disabled,
	defaultPageOptions,
	isAdmin,
	jurisdictionOptions,
	jurisdiction,
}) => {
	const [data, setData] = useState({});
	const updateField = (field, value) => {
		if (!field) return;
		if (field.includes("[")) field = setWith({}, field, value, Object);
		console.debug(field, value);
		const newData = merge(data, { ...(typeof field == "object" ? field : { [field]: value }) });
		console.debug(newData);

		setData(cloneDeep(newData));
	};

	const onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();
		updateCompanyDetails(
			cleanDeep(
				{
					org: String(currentOrg),
					...data,
					mfaRequirement: data.mfaRequirement ? data.mfaRequirement : 0,
				},
				{
					emptyStrings: false,
					emptyArrays: false,
					emptyObjects: false,
				},
			),
		);
	};

	useEffect(() => {
		if (!settings.org) return;

		setData({
			mfaRequirement: settings?.mfaRequirement,
			portalSettings: settings?.portalSettings,
		});
	}, [settings?.org, jurisdictionOptions?.length]);

	return (
		<form
			className="px-8"
			onSubmit={onSubmit}
		>
			<div className="form-section row-span-1">
				<div className="col-xs-12 form-container">
					<h4>Multi-Factor Authentication</h4>
				</div>
				<div className="col-xs-12">
					<div className="col-xs-12 form-container pl-0">
						<label className="css-input switch switch-sm switch-success">
							<input
								type="checkbox"
								className="checkbox-toggle"
								disabled={disabled}
								checked={!!data?.mfaRequirement}
								onChange={(e) => {}}
								onClick={(e) => {
									if (data?.mfaRequirement && !hasMFA) {
										setMFAModalStatus(true);
									}
									updateField("mfaRequirement", !data.mfaRequirement ? 1 : 0);
								}}
							/>
							<span />
							Enable MFA
						</label>
					</div>

					{data?.mfaRequirement ? (
						<div className="form-container col-xs-12 pl-0 pr-0">
							<label>Enforce MFA for</label>
							<Select
								options={mfaOptions}
								name="mfaRequirement"
								isDisabled={disabled}
								value={mfaOptions.find((i) => String(i.value) === String(data?.mfaRequirement))}
								placeholder="Choose one"
								onChange={(selected) => {
									updateField("mfaRequirement", selected?.value || null);
								}}
							/>
						</div>
					) : null}
				</div>
				<div className="clearfix" />
			</div>

			<div className="form-section row-span-1 pb-8">
				<div className="col-xs-12 form-container">
					<h4>Employee Portal Settings</h4>
				</div>
				<div className="col-xs-12">
					<div className="col-xs-6 px-0">
						<label>Default Page</label>
						<Select
							options={defaultPageOptions}
							value={defaultPageOptions.find((i) => i.value == data?.portalSettings?.defaultPage)}
							onChange={(e) => {
								updateField("portalSettings[defaultPage]", e?.value || null);
							}}
						/>
					</div>
				</div>
				<div className="clearfix" />
			</div>

			<div className="col-span-3">
				{!disabled && (
					<div className="text-right">
						<button
							className="btn btn-success"
							type="submit"
						>
							{saving ? (
								<Spinner
									style={{ height: "1.5em", float: "left", marginRight: 5, float: "left" }}
									className="animate-spin -ml-1 mr-3 h-5 w-5"
								/>
							) : (
								<CheckCircleIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
							)}
							<div style={{ float: "left" }}>{saving ? "Saving..." : "Save"}</div>
						</button>
					</div>
				)}
				<div className="clearfix" />
			</div>
			<div className="clearfix" />
		</form>
	);
};

const mapStateToProps = (state) => {
	const showLeave =
		state.leave?.leaveSettings?.leaveTypes?.some(
			(leaveType) => leaveType.showBalance || leaveType.employeeCanApply,
		) || false;

	const showWorkLocations = state?.workLocation?.workLocationSettings?.types?.length > 0;

	const defaultPageOptions = [
		{
			value: "payslips",
			label: "Payslips",
			available: true,
		},
		{
			value: "tax-forms",
			label: "Tax Forms",
			available: true,
		},
		{
			value: "timesheets",
			label: "Timesheets",
			available: state.company?.settings?.employeeViewTimesheets || false,
		},
		{
			value: "leave",
			label: "Leave",
			available: showLeave,
		},
		{
			value: "work-locations",
			label: "Work Locations",
			available: showWorkLocations,
		},
	];

	return {
		isAdmin: state.my?.profile?.isAdmin,
		profile: state?.my?.profile,
		hasMFA: state?.my?.profile?.hasMFA,
		settings: state.company?.settings,
		currentOrg: state.company?.settings?.org,
		jurisdictionOptions: (state.jurisdictions || [])?.map(({ countryCode, name }) => ({
			label: `${name}/${countryCode}`,
			value: countryCode,
		})),
		disabled:
			state?.permissions?.my?.permissions?.settings == "Read" || state?.company?.settings?.status == "Archived",
		saving: state.company?.saving,
		jurisdiction: state.company?.settings?.jurisdiction,
		companyAdvisor: state.company?.advisor,
		isEnterprise: state.company?.advisor?.bureau === "Enterprise",
		clientCanReview: state.permissions?.plan?.clientReview,
		defaultPageOptions: defaultPageOptions.filter((i) => i.available),
	};
};

const mapDispatchToProps = (dispatch) => ({
	setHMRCCredentialsModalStatus: (status) => dispatch(COMPANY_ACTIONS.setHMRCCredentialsModalStatus(status)),
	employeesGetList: () => dispatch(EMPLOYEEACTIONS.employeesGetList()),
	updateCompanyDetails: (body) => dispatch(COMPANY_ACTIONS.updateCompanyDetails(body)),
	handleLogoUpload: (file, org) => dispatch(COMPANY_ACTIONS.handleLogoUpload({ file, org })),
	setMFAModalStatus: (status) => dispatch(AUTHENTICATION_ACTIONS.setMFAModalStatus(status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AccessSettings);
